import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber', 'closeButton'];

  #buyButtonsIntersectionObserver = null;
  #resetTimeout;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #isStuck = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // ✅ closeボタン
    this.refs.closeButton?.addEventListener('click', () => {
      this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // 🔥 これが重要（元コードにあったやつ）
    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        const rect = entry.target.getBoundingClientRect();

        if (!entry.isIntersecting && rect.bottom < 0 && !this.#isStuck) {
          this.#showStickyBar();
        }

        if (entry.isIntersecting && this.#isStuck) {
          this.#hideStickyBar();
        }
      },
      { threshold: 0 }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  }

  // ==========================
  // 🔥 カート連携処理（完全復元）
  // ==========================
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // puppetモード
    this.#targetAddToCartButton.dataset.puppet = 'true';

    // 元ボタンを実行
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.productImage) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newStickyAddToCart =
      event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar =
      newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck =
      this.refs.stickyBar.getAttribute('data-stuck') || 'false';

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector('[ref="addToCartButton"]');
    }

    this.#updateButtonText();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #handleCartAddComplete = () => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
